/**
 * @file     LocationScoresSection.tsx
 * @readme   ../../CLAUDE.md
 * @purpose  Neighbourhood map and mobility block: the listing's map at radius
 *           20 with a thin-glass "Open the map" link to /properties filtered
 *           to the place, beside the walk and bike indices and commute times.
 *           The mapbox canvas (and its stylesheet) loads only once the frame
 *           nears the viewport.
 */
'use client';

import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import type { ListingDetail } from '@/services/mls/mlsPropertyService';
import { propertiesHrefForCity } from '@/core/config/bcAreas';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { hairline, hubColors } from '@/components/hub/hubKitStyles';
import { glassMaterial } from '@/design-system/tokens/glass';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { LocationScores, type LocationScoreData } from './LocationScores';

const ListingMapCanvas = dynamic(() => import('./ListingMapCanvas'), { ssr: false });

interface Props {
  detail: ListingDetail;
  address: string;
  locationScores: LocationScoreData | null;
}

export function LocationScoresSection({ detail, address, locationScores }: Props) {
  const { t } = useTranslation();
  const { theme, mode } = useAppTheme();
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [mapVisible, setMapVisible] = useState(false);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMapVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (detail.latitude == null || detail.longitude == null) return null;
  const mapHref = detail.city ? propertiesHrefForCity(detail.city, [detail.mlsAreaMajor]) : '/properties';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.25fr) minmax(0, 1fr)' },
        gap: { xs: 4, md: 6 },
        alignItems: 'start',
      }}
    >
      <Box
        ref={frameRef}
        id="listing-map-section"
        role="img"
        aria-label={t('listingDetail.location.mapLabel', { defaultValue: 'Map of {{address}}', address })}
        sx={{
          position: 'relative',
          aspectRatio: '4 / 3',
          borderRadius: `${layoutTokens.radius.lg}px`,
          border: hairline,
          overflow: 'hidden',
          isolation: 'isolate',
          backgroundColor: hubColors.surfaceSunken,
        }}
      >
        {mapVisible ? (
          <ListingMapCanvas
            pin={{
              latitude: detail.latitude,
              longitude: detail.longitude,
              title: address,
              price: detail.listPrice,
              beds: detail.bedroomsTotal,
              baths: detail.bathroomsTotalInteger,
              sqft: detail.livingArea,
            }}
            isDark={mode === 'dark'}
            markerColor={theme.rawSemanticColors.interactivePrimary}
          />
        ) : null}
        <Box
          component={Link}
          href={mapHref}
          sx={{
            position: 'absolute',
            left: 12,
            bottom: 12,
            zIndex: 1,
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: 44,
            px: 4,
            borderRadius: `${layoutTokens.radius.md}px`,
            ...glassMaterial('thin'),
            color: hubColors.textPrimary,
            fontSize: 15,
            fontWeight: fontWeightTokens.medium,
            textDecoration: 'none',
            '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 2 },
          }}
        >
          {t('listingDetail.location.openMap', { defaultValue: 'Open the map' })}
        </Box>
      </Box>
      <LocationScores
        scores={locationScores ?? { walkTimeS: null, transitTimeS: null, driveTimeS: null }}
        listingKey={detail.listingKey}
      />
    </Box>
  );
}
